import type { GameId } from '../wasm';

export interface GameMeta {
  label: string;
  short: string;
  iconSlug: string;
  firstColor: string | null;
  secondColor: string | null;
  firstIcon: string | null;
  secondIcon: string | null;
  hasSwap: boolean;
}

const META: Record<GameId, GameMeta> = {
  Abalone: { label: "アバロン", short: "アバロン", iconSlug: "abalone", firstColor: "黒", secondColor: "白", firstIcon: "●", secondIcon: "○", hasSwap: false },
  ChuShogi: { label: "中将棋", short: "中将棋", iconSlug: "chu-shogi", firstColor: "☗", secondColor: "☖", firstIcon: "☗", secondIcon: "☖", hasSwap: false },
  Kyoen: { label: "共円", short: "共円", iconSlug: "kyoen", firstColor: null, secondColor: null, firstIcon: null, secondIcon: null, hasSwap: false },
  Gomoku: { label: "五目並べ（Swap2）", short: "五目", iconSlug: "gomoku", firstColor: "黒", secondColor: "白", firstIcon: "●", secondIcon: "○", hasSwap: true },
  BridgeNT: { label: "2人制NTブリッジ", short: "ブリッジ", iconSlug: "bridge-nt", firstColor: "NS", secondColor: "EW", firstIcon: "NS", secondIcon: "EW", hasSwap: false },
  Trax: { label: "トラックス", short: "トラックス", iconSlug: "trax", firstColor: "白", secondColor: "赤", firstIcon: "○", secondIcon: "●", hasSwap: false },
  Amazons: { label: "アマゾンズ", short: "アマゾンズ", iconSlug: "amazons", firstColor: "白", secondColor: "黒", firstIcon: "○", secondIcon: "●", hasSwap: false },
  Renju:        { label: '連珠（五目並べ）', short: '連珠',     iconSlug: 'renju',          firstColor: '黒', secondColor: '白', firstIcon: '●', secondIcon: '○', hasSwap: true },
  AnimalShogi:  { label: 'どうぶつしょうぎ', short: 'どうぶつ', iconSlug: 'animal-shogi',   firstColor: null,  secondColor: null,  firstIcon: null,  secondIcon: null,  hasSwap: false },
  Reversi:      { label: 'リバーシ',         short: 'リバーシ', iconSlug: 'reversi',        firstColor: '黒', secondColor: '白', firstIcon: '●', secondIcon: '○', hasSwap: false },
  Chess:        { label: 'チェス',           short: 'チェス',   iconSlug: 'chess',          firstColor: '白', secondColor: '黒', firstIcon: '○', secondIcon: '●', hasSwap: false },
  Jungo:        { label: '囲碁',             short: '囲碁',     iconSlug: 'jungo',          firstColor: '黒', secondColor: '白', firstIcon: '●', secondIcon: '○', hasSwap: false },
  Shogi:        { label: '将棋',             short: '将棋',     iconSlug: 'shogi',          firstColor: '☗',  secondColor: '☖',  firstIcon: '☗', secondIcon: '☖', hasSwap: false },
  Backgammon:   { label: 'バックギャモン',   short: 'ギャモン', iconSlug: 'backgammon',     firstColor: null,  secondColor: null,  firstIcon: null,  secondIcon: null,  hasSwap: false },
  Connect6:     { label: 'コネクト6',        short: 'コネクト6',    iconSlug: 'connect6',       firstColor: '黒', secondColor: '白', firstIcon: '●', secondIcon: '○', hasSwap: false },
  TwixT:        { label: 'TwixT',            short: 'TwixT',    iconSlug: 'twixt',          firstColor: '黒', secondColor: '白', firstIcon: '●', secondIcon: '○', hasSwap: false },
  Xiangqi:      { label: 'シャンチー',       short: 'シャンチー', iconSlug: 'xiangqi',      firstColor: '紅', secondColor: '黒', firstIcon: '紅', secondIcon: '黑', hasSwap: false },
  Janggi:       { label: 'チャンギ',         short: 'チャンギ', iconSlug: 'janggi',         firstColor: '楚', secondColor: '漢', firstIcon: '楚', secondIcon: '漢', hasSwap: false },
  Draughts:     { label: 'ドラフツ',         short: 'ドラフツ', iconSlug: 'draughts',       firstColor: '白', secondColor: '黒', firstIcon: '○', secondIcon: '●', hasSwap: false },
  Kalah:        { label: 'マンカラ',         short: 'マンカラ', iconSlug: 'kalah',          firstColor: null,  secondColor: null,  firstIcon: null,  secondIcon: null,  hasSwap: false },
  HeadsUpPoker: { label: 'テキサスホールデム', short: 'ポーカー', iconSlug: 'heads-up-poker', firstColor: null,  secondColor: null,  firstIcon: null,  secondIcon: null,  hasSwap: false },
};

/**
 * 表示順。 1 段目 = mixed-7 preset の盤順、 2 段目 = new-7 preset の盤順。
 * Home の hero band / GamePicker のアイコングリッド / チュートリアル一覧で共通に使う。
 */
export const NEXT_SEVEN_GAME_ORDER: readonly GameId[] = ['Kyoen', 'Gomoku', 'Trax', 'Amazons', 'Abalone', 'ChuShogi', 'BridgeNT'];

export const GAME_ORDER: readonly GameId[] = [
  'Renju',
  'AnimalShogi',
  'Reversi',
  'Chess',
  'Jungo',
  'Shogi',
  'Backgammon',
  'Connect6',
  'TwixT',
  'Xiangqi',
  'Janggi',
  'Draughts',
  'Kalah',
  'HeadsUpPoker',
  ...NEXT_SEVEN_GAME_ORDER,
];

/** URL / アイコンの slug (`animal-shogi` 等) から GameId を引く。 未知なら null。 */
export function gameIdForSlug(slug: string): GameId | null {
  for (const id of GAME_ORDER) {
    if (META[id].iconSlug === slug) return id;
  }
  return null;
}

/**
 * 複数盤プリセットの表示名。 短い名前 (`short`) を「+」 で繋ぎ、 先頭に盤数を付ける:
 * 「3盤 どうぶつ + リバーシ + ギャモン」。 全盤が同じゲームなら 「バックギャモン × 3」。
 */
export function presetLabel(games: readonly GameId[]): string {
  if (games.length > 1 && games.every((g) => g === games[0])) {
    return `${gameLabel(games[0])} × ${games.length}`;
  }
  return `${games.length}盤 ${games.map(gameShort).join(' + ')}`;
}

export function gameMeta(id: GameId): GameMeta {
  return META[id] ?? { label: String(id), short: String(id), iconSlug: String(id).toLowerCase(), firstColor: null, secondColor: null, firstIcon: null, secondIcon: null, hasSwap: false };
}

export function gameShort(id: GameId): string {
  return gameMeta(id).short;
}

export function gameLabel(id: GameId): string {
  return gameMeta(id).label;
}

export function gameIconSlug(id: GameId): string {
  return gameMeta(id).iconSlug;
}

/**
 * プレイ中に盤の上へ 1 行だけ出す目的文。 `goal` が「何をすれば勝ちか」、
 * `note` は初見で引っかかる主要ルールを 1 つだけ。 note は省略可。
 * 長い説明はチュートリアルの役目なので、 ここは 1 行に収まる長さを保つ。
 */
export interface GameObjective {
  goal: string;
  note?: string;
}

const OBJECTIVES: Record<GameId, GameObjective> = {
  Abalone: {goal: "相手の玉を6個盤外へ押し出す", note: "1〜3個を選んで移動"},
  ChuShogi: {goal: "相手の王・太子を取り切る", note: "獅子は二段移動できる"},
  Kyoen: {goal: "最後の石を含む4点の円か直線を見つける"},
  Gomoku: {goal: "自分の色をちょうど5つ並べる", note: "Swap2で色を選ぶ"},
  BridgeNT: {goal: "理想より多くトリックを取る", note: "同じスートがあれば従う"},
  Trax: {goal: "自分の色で輪か8マス以上の横断線を作る", note: "強制タイルは自動で置かれる"},
  Amazons: {goal: "相手が動けなくなれば勝ち", note: "駒→移動先→矢の順で選ぶ"},
  Renju:        { goal: '自分の石を5つ並べる',       note: '黒は禁手に注意' },
  AnimalShogi:  { goal: 'ライオンを取る',              note: 'ライオンを1段目に上げても勝ち' },
  Reversi:      { goal: 'たくさん石を置く',            note: '挟んでひっくり返す' },
  Chess:        { goal: '相手のキングを詰ませる' },
  Jungo:        { goal: 'たくさん石を置く',            note: '囲まれると消える' },
  Shogi:        { goal: '相手の玉を詰ませる' },
  Backgammon:   { goal: 'ダイス2つで反時計回りにコマを進める' },
  Connect6:     { goal: '自分の石を6つ並べる',       note: '1手に2つ置く' },
  TwixT:        { goal: '自分の色の2辺をつなぐ',     note: '桂馬の位置に橋が架かる' },
  Xiangqi:      { goal: '相手の将を詰ませる',          note: '砲は駒を1つ飛び越えて取る' },
  Janggi:       { goal: '相手の将を詰ませる',          note: 'パスできる' },
  Draughts:     { goal: '相手のコマを全部取る',        note: '取れるときは最多の手を選ぶ' },
  Kalah:        { goal: '自分のストアに多く石を集める', note: '反時計回りに1個ずつ撒く' },
  HeadsUpPoker: { goal: '相手のチップを全部奪う',      note: '7枚から5枚の役を作る' },
};

export function gameObjective(id: GameId): GameObjective | null {
  return OBJECTIVES[id] ?? null;
}

/**
 * 複数盤プリセットの定義 (id → 盤の並び)。 template 名は `preset:<id>`。
 * GamePicker のプリセット行はこの順に並ぶ (盤数の少ない順、 同数なら登録順)。
 * 表示名は `presetLabel(games)` で組み立てる (= 直書きしない)。
 */
export const MULTI_PRESET_DEFS: readonly { id: string; games: readonly GameId[] }[] = [
  { id: 'mixed-3', games: ['AnimalShogi', 'Reversi', 'Backgammon'] },
  { id: 'classic-3', games: ['Shogi', 'Jungo', 'Reversi'] },
  { id: 'connect-3', games: ['Jungo', 'Renju', 'Connect6'] },
  { id: 'backgammon-x3', games: ['Backgammon', 'Backgammon', 'Backgammon'] },
  { id: 'chess-4', games: ['Chess', 'Shogi', 'Xiangqi', 'Janggi'] },
  { id: 'mixed-5', games: ['Renju', 'AnimalShogi', 'Reversi', 'Chess', 'Backgammon'] },
  { id: 'mixed-7', games: ['Renju', 'AnimalShogi', 'Reversi', 'Chess', 'Jungo', 'Shogi', 'Backgammon'] },
  { id: 'new-7', games: ['Connect6', 'TwixT', 'Xiangqi', 'Janggi', 'Draughts', 'Kalah', 'HeadsUpPoker'] },
  // User-corrected left-to-right order; no measured expected-move metadata exists.
  // Bridge stays rightmost; keep the older presets unchanged.
  { id: 'next-7', games: NEXT_SEVEN_GAME_ORDER },
];

/** 旧データにだけ残る template (ピッカーには出さない)。 */
const LEGACY_TEMPLATE_GAMES: Record<string, readonly GameId[]> = {
  'preset:animal-reversi': ['AnimalShogi', 'Reversi'],
};

/**
 * template 名 (`preset:renju` / `preset:mixed-3` 等) の表示名。
 * 単一ゲームは `label`、 複数盤は `presetLabel` で、 GamePicker と同じ文字列を返す。
 */
export function templateLabel(template: string): string {
  const slug = template.replace(/^preset:/, '');
  const preset = MULTI_PRESET_DEFS.find((p) => p.id === slug);
  if (preset) return presetLabel(preset.games);
  const legacy = LEGACY_TEMPLATE_GAMES[template];
  if (legacy) return presetLabel(legacy);
  if (slug === 'animal') return gameLabel('AnimalShogi');
  const id = gameIdForSlug(slug);
  if (id) return gameLabel(id);
  return template;
}

export function firstPlayerLabel(id: GameId, swapped: boolean): string | null {
  const m = gameMeta(id);
  if (!m.firstColor) return null;
  const color = swapped ? m.secondColor : m.firstColor;
  const suffix = m.hasSwap && swapped ? ' (仮先)' : '';
  return `先手(${color})${suffix}`;
}

export interface SideDisplay {
  side: string;
  /** コマの色アイコン。 色の無いゲーム (どうぶつ・ギャモン・マンカラ) は null。 */
  icon: string | null;
  swap: string | null;
}

/**
 * @param mySide  物理色 (swap 反映後)。 'First' = 今 先手の駒色を持っている。
 * @param swapped swap が発生したか (= swap_count が奇数)。
 *
 * 仮先/仮後は「元の slot (swap 前) でどちら側だったか」を示す:
 *   swap なし: First→仮先, Second→仮後
 *   swap あり: First→仮後 (swap で先手を取った), Second→仮先 (最初に打ったが奪われた)
 */
export function mySideDisplay(id: GameId, mySide: 'First' | 'Second', swapped = false): SideDisplay | null {
  const m = gameMeta(id);
  // ポーカーは先手/後手の概念が無い (ボタンは交代する) ので出さない。
  if (id === 'HeadsUpPoker') return null;
  const isFirst = mySide === 'First';
  const originallyFirst = swapped ? !isFirst : isFirst;
  // コマの色が無いゲーム (どうぶつ・ギャモン・マンカラ) でも 先手/後手 は出す
  // (以前はアイコンが無いと丸ごと非表示になり、 どうぶつ/ギャモンだけ手番が
  // 分からなかった)。
  const icon = isFirst ? m.firstIcon : m.secondIcon;
  return {
    side: isFirst ? '先手' : '後手',
    icon,
    swap: m.hasSwap ? (originallyFirst ? '仮先' : '仮後') : null,
  };
}
